import { useEffect, useMemo, useState } from 'react'

export type SortDir = 'asc' | 'desc'

/**
 * Get a value from a row by dot-path or via a custom accessor.
 * `accessor` wins if provided.
 */
export type Accessor<T> = string | ((row: T) => unknown)

function getValue<T>(row: T, acc: Accessor<T>): unknown {
  if (typeof acc === 'function') return acc(row)
  // Dot-path lookup; tolerant of nullish intermediate
  return acc.split('.').reduce<unknown>((cur, key) => {
    if (cur && typeof cur === 'object') return (cur as Record<string, unknown>)[key]
    return undefined
  }, row as unknown)
}

function compare(a: unknown, b: unknown): number {
  // null/undefined sort to the end regardless of direction (stable UX)
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)
  if (a instanceof Date || b instanceof Date) {
    const av = a instanceof Date ? a.getTime() : new Date(String(a)).getTime()
    const bv = b instanceof Date ? b.getTime() : new Date(String(b)).getTime()
    return av - bv
  }
  // String compare with locale-aware German collation
  return String(a).localeCompare(String(b), 'de', { sensitivity: 'base', numeric: true })
}

export interface UseDataTableOptions<T> {
  /** Fields searched against by the toolbar text input. Each is dot-path or accessor. */
  searchableFields?: Accessor<T>[]
  /** Default sort. */
  defaultSortKey?: string
  defaultSortDir?: SortDir
  /** Page size. Use 0 to disable pagination. */
  defaultPageSize?: number
  /** Stable storage key — if provided, page-size and sort-pref persist across reloads. */
  storageKey?: string
  /** How to derive a row ID for selection. Defaults to `(row) => row.id`. */
  rowId?: (row: T) => string
}

export interface UseDataTableResult<T> {
  // current view (filtered, sorted, paged)
  rows: T[]
  // counts
  totalRows: number
  filteredRows: number
  // search
  search: string
  setSearch: (q: string) => void
  // sort
  sortKey: string | null
  sortDir: SortDir
  toggleSort: (key: string) => void
  setSort: (key: string, dir: SortDir) => void
  // pagination
  page: number
  pageSize: number
  totalPages: number
  setPage: (p: number) => void
  setPageSize: (n: number) => void
  // selection
  selectedIds: Set<string>
  selectionCount: number
  isSelected: (id: string) => boolean
  toggleRow: (id: string) => void
  toggleAllOnPage: () => void
  selectAllFiltered: () => void
  clearSelection: () => void
  allOnPageSelected: boolean
  someOnPageSelected: boolean
  // helpers
  hasActiveFilters: boolean
  reset: () => void
}

/**
 * Pure client-side table utilities: search / sort / pagination over an array.
 * Stable enough for a few thousand rows; for larger sets switch to server-side.
 */
export function useDataTable<T>(
  data: T[],
  /** Map sort-key → accessor. Keys not in the map fall back to dot-path lookup. */
  sortAccessors: Record<string, Accessor<T>> = {},
  options: UseDataTableOptions<T> = {},
): UseDataTableResult<T> {
  const {
    searchableFields = [],
    defaultSortKey = null,
    defaultSortDir = 'asc',
    defaultPageSize = 25,
    storageKey,
    rowId = (row: T) => (row as unknown as { id: string }).id,
  } = options

  // Hydrate persisted prefs once. Defaults are captured on mount; we only
  // re-run if the storageKey itself changes (rare).
  // biome-ignore lint/correctness/useExhaustiveDependencies: defaults captured on mount on purpose
  const initial = useMemo(() => {
    if (!storageKey || typeof window === 'undefined') {
      return { sortKey: defaultSortKey, sortDir: defaultSortDir, pageSize: defaultPageSize }
    }
    try {
      const raw = window.localStorage.getItem(`dt:${storageKey}`)
      if (!raw)
        return { sortKey: defaultSortKey, sortDir: defaultSortDir, pageSize: defaultPageSize }
      const parsed = JSON.parse(raw) as { sortKey?: string; sortDir?: SortDir; pageSize?: number }
      return {
        sortKey: parsed.sortKey ?? defaultSortKey,
        sortDir: parsed.sortDir ?? defaultSortDir,
        pageSize: typeof parsed.pageSize === 'number' ? parsed.pageSize : defaultPageSize,
      }
    } catch {
      return { sortKey: defaultSortKey, sortDir: defaultSortDir, pageSize: defaultPageSize }
    }
  }, [storageKey])

  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<string | null>(initial.sortKey)
  const [sortDir, setSortDir] = useState<SortDir>(initial.sortDir)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(initial.pageSize)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())

  // Persist when prefs change
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return
    try {
      window.localStorage.setItem(
        `dt:${storageKey}`,
        JSON.stringify({ sortKey, sortDir, pageSize }),
      )
    } catch {
      // ignore quota errors
    }
  }, [storageKey, sortKey, sortDir, pageSize])

  // Reset to first page only when a filter/sort/page-size knob moves.
  // data.length is deliberately NOT a dep: a background refetch (e.g. the
  // 5s Belegeingang poll) or a row deleted by a colleague would otherwise
  // yank the user back to page 1. Overflow is handled by clamping to
  // safePage below, so the current page stays valid without a reset.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are reset triggers, not values read in the body
  useEffect(() => {
    setPage(1)
  }, [search, sortKey, sortDir, pageSize])

  // The caller is expected to pass stable arrays/objects for searchableFields
  // and sortAccessors (they're captured on mount in practice). We keep them out
  // of the deps array to avoid re-filtering on every parent render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: searchableFields treated as stable
  const filtered = useMemo(() => {
    if (!search.trim() || searchableFields.length === 0) return data
    const q = search.trim().toLowerCase()
    return data.filter((row) =>
      searchableFields.some((acc) => {
        const v = getValue(row, acc)
        if (v == null) return false
        return String(v).toLowerCase().includes(q)
      }),
    )
  }, [data, search])

  // biome-ignore lint/correctness/useExhaustiveDependencies: sortAccessors treated as stable
  const sorted = useMemo(() => {
    if (!sortKey) return filtered
    const acc = sortAccessors[sortKey] ?? sortKey
    const arr = [...filtered]
    arr.sort((a, b) => {
      const av = getValue(a, acc)
      const bv = getValue(b, acc)
      const c = compare(av, bv)
      return sortDir === 'asc' ? c : -c
    })
    return arr
  }, [filtered, sortKey, sortDir])

  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1
  const safePage = Math.min(page, totalPages)
  const rows = pageSize > 0 ? sorted.slice((safePage - 1) * pageSize, safePage * pageSize) : sorted

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const setSort = (key: string, dir: SortDir) => {
    setSortKey(key)
    setSortDir(dir)
  }

  const reset = () => {
    setSearch('')
    setSortKey(defaultSortKey)
    setSortDir(defaultSortDir)
    setPage(1)
    setSelectedIds(new Set())
  }

  // === Selection ===

  const visibleIds = rows.map(rowId)
  const filteredAllIds = sorted.map(rowId)
  const allOnPageSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))
  const someOnPageSelected = visibleIds.some((id) => selectedIds.has(id)) && !allOnPageSelected

  const isSelected = (id: string) => selectedIds.has(id)

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) {
        for (const id of visibleIds) next.delete(id)
      } else {
        for (const id of visibleIds) next.add(id)
      }
      return next
    })
  }

  const selectAllFiltered = () => {
    setSelectedIds(new Set(filteredAllIds))
  }

  const clearSelection = () => setSelectedIds(new Set())

  // Drop selection IDs that are no longer in the current dataset (e.g. after
  // server-side delete) so the count stays accurate. We intentionally only
  // depend on `data` — running this on every `selectedIds` change would loop.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    if (selectedIds.size === 0) return
    const allIds = new Set(data.map(rowId))
    let changed = false
    const next = new Set<string>()
    for (const id of selectedIds) {
      if (allIds.has(id)) next.add(id)
      else changed = true
    }
    if (changed) setSelectedIds(next)
  }, [data])

  return {
    rows,
    totalRows: data.length,
    filteredRows: sorted.length,
    search,
    setSearch,
    sortKey,
    sortDir,
    toggleSort,
    setSort,
    page: safePage,
    pageSize,
    totalPages,
    setPage,
    setPageSize,
    selectedIds,
    selectionCount: selectedIds.size,
    isSelected,
    toggleRow,
    toggleAllOnPage,
    selectAllFiltered,
    clearSelection,
    allOnPageSelected,
    someOnPageSelected,
    hasActiveFilters: search.trim().length > 0,
    reset,
  }
}
